import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Tendência do custo: colunas empilhadas, transcrição embaixo e LLM em cima, então a
 * ALTURA da coluna é o custo total. Duas séries dão as três leituras que interessam
 * (total, só LLM, só transcrição) com dois marcadores em vez de três linhas — a terceira
 * linha seria a soma das outras duas, redundante e mais difícil de ler.
 *
 * Transcrição vai embaixo de propósito: ela é estritamente linear na duração, então é a
 * parcela estável. Assim toda a variação aparece no topo da coluna, que é onde o olho lê.
 *
 * SVG inline, sem biblioteca de gráfico: são duas séries e o app não tem nenhuma hoje.
 * Largura medida por ResizeObserver em vez de viewBox escalável, para o texto não crescer
 * junto com o container.
 *
 * Paleta: slots categóricos 1 (azul) e 2 (laranja) do tema claro. Validada — CVD ΔE 24,7
 * e visão normal ΔE 33,6, acima dos pisos de 8 e 15.
 */

const COR_STT = '#eb6834';    // slot 2 — laranja
const COR_LLM = '#2a78d6';    // slot 1 — azul
const COR_INFRA = '#1baf7a';  // slot 3 — verde-água
const SUPERFICIE = '#ffffff';
const GRADE = '#e9ecef';
const EIXO = '#adb5bd';
const TEXTO_2 = '#6c757d';
const TEXTO_1 = '#212529';

const ALTURA = 300;
const M = { top: 26, right: 104, bottom: 38, left: 62 };  // top folgado p/ rotulo de recorte
const BARRA_MAX = 24;
const BANDA_MAX = 88;        // com poucas colunas, encurta o plot em vez de espalhar ar

/**
 * Piso de volume para um bucket entrar na TENDÊNCIA (não para ser desenhado).
 *
 * Custo por hora é uma razão: com denominador minúsculo ela não mede nada. Abril e maio
 * de 2026 têm UMA gravação cada (oticascarol, 5,8 e 8,9 min) e dão R$ 1,59/h contra
 * R$ 4,12/h — 2,6x de diferença que é só ruído de amostra. Dois pontos desses puxando a
 * reta valem mais que os meses de verdade.
 *
 * Horas, e não contagem de atendimentos, porque é hora o denominador da métrica.
 */
const MIN_HORAS_TENDENCIA = 10;

/**
 * Piso de volume para um bucket DEFINIR A ESCALA do eixo, por granularidade.
 *
 * Espelha `_INFRA_MIN_HORAS_BUCKET` da API, que decide quais buckets recebem a parcela fixa
 * de infra. Precisa ser por granularidade: um piso único de 50 h excluiria quase todo dia do
 * gráfico diário e recortaria barras legítimas.
 *
 * Com a API já não atribuindo infra a bucket sem denominador, o outlier gigante desapareceu
 * na origem; este piso sobra como cinto de segurança para o que ainda destoar — e o excesso
 * é recortado no teto, com o valor real rotulado em cima, nunca escondido.
 */
const MIN_HORAS_ESCALA = { day: 2, week: 10, month: 50 };
const VAO = 2;               // vão na cor da superfície entre os dois segmentos

const PASSOS = [0.02, 0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500];

const escalaBoa = (max) => {
    for (const p of PASSOS) {
        if (max / p <= 5) return { passo: p, topo: Math.ceil(max / p) * p || p };
    }
    const p = PASSOS[PASSOS.length - 1];
    return { passo: p, topo: Math.ceil(max / p) * p };
};

/**
 * Reta de mínimos quadrados sobre pares [índice do bucket, valor].
 *
 * Recebe PARES, e não uma lista de valores, porque o eixo pode ter buckets vazios (mês
 * sem atendimento nos 12 meses): tratá-los como zero puxaria a reta para baixo e
 * inventaria uma queda que não houve. O índice preservado é o que mantém a inclinação
 * "por mês" e não "por mês com dado".
 *
 * Devolve null com menos de três pontos: com dois, "tendência" é só o segmento que liga
 * os dois — a palavra prometeria mais do que o dado sustenta.
 */
const tendenciaLinear = (pares) => {
    const n = pares.length;
    if (n < 3) return null;
    let sx = 0, sy = 0, sxy = 0, sxx = 0;
    pares.forEach(([x, y]) => { sx += x; sy += y; sxy += x * y; sxx += x * x; });
    const den = n * sxx - sx * sx;
    if (den === 0) return null;
    const inclinacao = (n * sxy - sx * sy) / den;
    const intercepto = (sy - inclinacao * sx) / n;
    const xs = pares.map(([x]) => x);
    return {
        inclinacao, intercepto, n,
        de: Math.min(...xs), ate: Math.max(...xs),
        em: (x) => intercepto + inclinacao * x,
    };
};

const fmtBRL = (v, dec = 2) =>
    `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const rotulo = (bucket, gran) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(bucket || '');
    if (!m) return bucket || '';
    if (gran === 'month') return `${MESES[Number(m[2]) - 1]}/${m[1].slice(2)}`;
    return `${m[3]}/${m[2]}`;
};

/**
 * Em que coluna cai um marco.
 *
 * Casa pela ÚLTIMA coluna cujo início é <= a data do marco, o que resolve as três
 * granularidades com uma regra só: em `month` o marco de 23/08 cai em ago/26, em `week` cai
 * na semana que o contém. Em `day` exige igualdade — atribuir um marco de um dia sem coluna
 * ao dia anterior inventaria uma causa no bucket errado, e é justo no diário que o leitor
 * mais confia na precisão da data.
 *
 * Devolve -1 quando o marco é anterior à primeira coluna: nesse caso ele é contexto do
 * período, não um evento dentro dele, e desenhá-lo na borda sugeriria que aconteceu ali.
 */
const colunaDoMarco = (data, points, gran) => {
    let idx = -1;
    for (let i = 0; i < points.length; i += 1) {
        if (points[i].bucket && points[i].bucket <= data) idx = i;
    }
    if (idx < 0) return -1;
    if (gran === 'day' && points[idx].bucket !== data) return -1;
    return idx;
};

function GraficoCustos({ points, granularity, taxa, modo, meta = null, tendencia = false,
                         marcos = [] }) {
    const box = useRef(null);
    const [largura, setLargura] = useState(960);
    const [hover, setHover] = useState(null);

    // Mesmo cuidado do GraficoNos: o `return` antecipado do estado vazio acontece antes de o
    // ref existir, e `taxa` comeca nula. Sem depender de [points, taxa], o observer nunca era
    // instalado e a largura ficava travada no valor inicial.
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([e]) => setLargura(Math.max(420, e.contentRect.width)));
        const el = box.current;
        if (el) ro.observe(el);
        return () => ro.disconnect();
    }, [points, taxa]);

    // A infra do gráfico é SÓ a medida por uso. Capacidade contratada e acervo não mudam
    // porque entrou um atendimento, então empilhá-las aqui misturava custo marginal com
    // custo de estrutura — e como a capacidade é ~93% da infra, ela dominava a coluna e
    // escondia justamente o que o gráfico existe para mostrar. Ela tem bloco próprio na
    // tela.
    //
    // `temQuebra` guarda contra API antiga: sem os campos novos, volta ao comportamento
    // anterior (infra inteira) em vez de desenhar zero e mentir por omissão.
    const temQuebra = points?.some?.((p) => p.infra_variable_cost_usd != null) ?? false;
    const infraDe = useCallback((p) => (
        temQuebra ? (p.infra_variable_cost_usd || 0) : (p.infra_cost_usd || 0)
    ), [temQuebra]);
    const totalDe = useCallback((p) => (
        temQuebra ? (p.demand_cost_usd || 0) : (p.total_cost_usd || 0)
    ), [temQuebra]);

    const valor = useCallback((p, qual) => {
        let bruto;
        if (modo === 'unitario') {
            const n = p.transcriptions || 0;
            bruto = n > 0
                ? { infra: infraDe(p) / n, stt: p.avg_stt_usd, llm: p.avg_llm_usd,
                    total: (temQuebra ? p.avg_demand_usd : p.avg_total_usd) }
                : { infra: null, stt: null, llm: null, total: null };
        } else if (modo === 'hora') {
            // Custo por hora de áudio: a única leitura comparável entre meses, porque não
            // se move com a mistura de durações nem com o volume.
            const h = p.hours || 0;
            bruto = h > 0
                ? { infra: infraDe(p) / h, stt: p.stt_cost_usd / h,
                    llm: p.llm_cost_usd / h, total: totalDe(p) / h }
                : { infra: null, stt: null, llm: null, total: null };
        } else {
            bruto = { infra: infraDe(p), stt: p.stt_cost_usd, llm: p.llm_cost_usd,
                      total: totalDe(p) };
        }
        const v = bruto[qual];
        return v == null || !taxa ? 0 : v * taxa;
    }, [modo, taxa, infraDe, totalDe, temQuebra]);

    if (!points || points.length === 0) {
        return <div className="gc-wrap" ref={box}><div className="gc-vazio">Sem dados no período.</div></div>;
    }

    const temInfra = points.some((p) => infraDe(p) > 0);
    const unitario = modo === 'unitario';
    // A meta de R$ 1,00 é por HORA de atendimento. Só faz sentido no eixo por hora —
    // desenhá-la sobre custo por atendimento compararia coisas diferentes.
    const metaVisivel = modo === 'hora' && !!taxa && !!meta;

    // Só buckets com volume suficiente definem o teto do eixo; se nenhum tiver, usa todos.
    const pisoEscala = MIN_HORAS_ESCALA[granularity] ?? 0;
    const paraEscala = points.filter((p) => (p.hours || 0) >= pisoEscala);
    const baseEscala = paraEscala.length ? paraEscala : points;
    const maxDados = Math.max(...baseEscala.map((p) => valor(p, 'total')));
    // Em modo unitário o eixo acomoda a linha da meta: ela é parte do significado do dado,
    // e uma meta fora do quadro não informa nada.
    const { passo, topo } = escalaBoa(Math.max(maxDados, metaVisivel ? meta * 1.05 : 0) || 1);

    // Marcos agrupados por coluna: vários podem cair no mesmo mês (23, 24 e 25/08 caem
    // todos em ago/26), e três marcadores empilhados na mesma banda seriam ilegíveis. Um
    // marcador por coluna, rotulado com os números que ele carrega.
    const marcosPorColuna = new Map();
    marcos.forEach((m) => {
        const i = colunaDoMarco(m.data, points, granularity);
        if (i < 0) return;
        if (!marcosPorColuna.has(i)) marcosPorColuna.set(i, []);
        marcosPorColuna.get(i).push(m);
    });
    // A faixa dos marcadores só existe quando há marcador: sem isso o gráfico ganharia 22px
    // de ar em branco em toda janela que não contenha nenhum marco.
    const faixaMarcos = marcosPorColuna.size > 0 ? 22 : 0;
    const alturaSvg = ALTURA + faixaMarcos;
    const mBottom = M.bottom + faixaMarcos;

    const larguraUsada = Math.min(largura, M.left + M.right + points.length * BANDA_MAX);
    const larguraPlot = larguraUsada - M.left - M.right;
    const alturaPlot = alturaSvg - M.top - mBottom;
    const banda = larguraPlot / points.length;
    const larguraBarra = Math.min(BARRA_MAX, banda * 0.68);
    const y = (v) => M.top + alturaPlot - (v / topo) * alturaPlot;
    const xBanda = (i) => M.left + banda * i;
    const xBarra = (i) => xBanda(i) + (banda - larguraBarra) / 2;

    const ticks = [];
    for (let v = 0; v <= topo + 1e-9; v += passo) ticks.push(v);

    // Rótulo de eixo x em toda coluna vira ruído: mostra ~8 e sempre a última.
    const salto = Math.max(1, Math.ceil(points.length / (granularity === 'month' ? 12 : 8)));
    const ultimo = points[points.length - 1];

    // Bucket "ralo": tem dado, mas pouco — desenhado, e fora da tendência.
    const ralo = (p) => p.transcriptions > 0 && (p.hours || 0) < MIN_HORAS_TENDENCIA;
    const reta = tendencia
        ? tendenciaLinear(points
            .map((p, i) => [i, valor(p, 'total'), p])
            .filter(([, , p]) => p.transcriptions > 0 && !ralo(p))
            .map(([i, v]) => [i, v]))
        : null;
    const decimais = topo >= 100 ? 0 : 2;
    const sufixo = unitario ? ' / atendimento' : (modo === 'hora' ? ' / hora' : '');
    const cadencia = granularity === 'month' ? 'por mês'
        : (granularity === 'week' ? 'por semana' : 'por dia');

    return (
        <div className="gc-wrap" ref={box}>
            <div className="gc-legenda">
                <span className="gc-chave">
                    <span className="gc-swatch" style={{ background: COR_LLM }} />LLM
                </span>
                <span className="gc-chave">
                    <span className="gc-swatch" style={{ background: COR_STT }} />Transcrição
                </span>
                {temInfra && (
                    <span className="gc-chave">
                        <span className="gc-swatch" style={{ background: COR_INFRA }} />
                        {temQuebra ? 'Infra por uso' : 'Infra'}
                    </span>
                )}
                {reta && (
                    <span className="gc-chave">
                        <span className="gc-swatch-linha" />
                        tendência {reta.inclinacao >= 0 ? '+' : '−'}
                        {fmtBRL(Math.abs(reta.inclinacao))} / {cadencia.replace('por ', '')}
                        <span className="gc-legenda-nota">
                            {reta.em(reta.de) > 0 && (
                                <>{' '}({reta.inclinacao >= 0 ? '+' : '−'}
                                {Math.abs((reta.inclinacao / reta.em(reta.de)) * 100).toFixed(0)}%)</>
                            )}
                            {' · '}{reta.n} {cadencia === 'por mês' ? 'meses' : 'pontos'} com dado
                        </span>
                    </span>
                )}
                <span className="gc-legenda-nota">
                    altura da coluna = {temQuebra ? 'o que varia com a demanda' : 'total'}{sufixo}
                    {' · '}{cadencia}
                    {points.some((p) => valor(p, 'total') > topo * 1.001)
                        && ' · barra recortada traz o valor real em cima'}
                </span>
            </div>

            <svg width={larguraUsada} height={alturaSvg} role="img"
                 aria-label={`Tendência do custo${sufixo || ' total'}, transcrição e LLM`}>
                {/* superfície do plot: o vão de 2px entre os segmentos empilhados tem de ser a
                    cor da superfície, não o que estiver atrás do SVG */}
                <rect x={M.left} y={M.top} width={larguraPlot} height={alturaPlot} fill={SUPERFICIE} />

                {/* grade recessiva + ticks */}
                {ticks.map((v) => (
                    <g key={v}>
                        <line x1={M.left} x2={M.left + larguraPlot} y1={y(v)} y2={y(v)}
                              stroke={v === 0 ? EIXO : GRADE} strokeWidth={1} />
                        <text x={M.left - 8} y={y(v) + 4} textAnchor="end"
                              fontSize={11} fill={TEXTO_2}>
                            {fmtBRL(v, decimais)}
                        </text>
                    </g>
                ))}

                {/* Guia vertical de cada marco, desenhada ANTES das colunas para ficar atrás
                    delas: o marco é anotação sobre o dado, não pode cobrir a marca. Vai do
                    topo do plot até a faixa do marcador, ligando o número à coluna. */}
                {[...marcosPorColuna.keys()].map((i) => (
                    <line key={`mg-${i}`} x1={xBanda(i) + banda / 2} x2={xBanda(i) + banda / 2}
                          y1={M.top} y2={alturaSvg - mBottom + 22}
                          stroke={TEXTO_1} strokeWidth={1} strokeDasharray="3 3" opacity={0.28} />
                ))}

                {/* colunas */}
                {points.map((p, i) => {
                    const apagado = hover != null && hover !== i;
                    // Volume baixo demais para comparar: fica recessivo em vez de sumir.
                    const opacidade = (apagado ? 0.45 : 1) * (ralo(p) ? 0.45 : 1);
                    // Empilha de baixo para cima na ordem do menos controlável pelo pipeline
                    // (infra) ao mais controlável (LLM) — que é também o que varia mais, e
                    // variação se lê no topo da coluna.
                    const camadas = [
                        { v: valor(p, 'infra'), cor: COR_INFRA },
                        { v: valor(p, 'stt'), cor: COR_STT },
                        { v: valor(p, 'llm'), cor: COR_LLM },
                    ].filter((c) => c.v > 0);
                    const totalBarra = camadas.reduce((a, c) => a + c.v, 0);
                    const recortada = totalBarra > topo * 1.001;
                    let acumulado = 0;
                    return (
                        <g key={p.bucket} opacity={opacidade}>
                            {camadas.map((c, k) => {
                                const yBase = Math.max(M.top, y(acumulado));
                                acumulado += c.v;
                                // preso ao teto: barra recortada nao invade a area do titulo
                                const yTopo = Math.max(M.top, y(acumulado));
                                const ultimo = k === camadas.length - 1;
                                // o vão de 2px sai do segmento de baixo, na cor da superfície:
                                // é um vão, não uma borda desenhada em volta da marca
                                const alt = Math.max(0, yBase - yTopo - (ultimo ? 0 : VAO));
                                if (alt <= 0) return null;
                                return (
                                    <rect key={c.cor} x={xBarra(i)} y={yTopo} width={larguraBarra}
                                          height={alt} fill={c.cor}
                                          rx={ultimo && !recortada ? 4 : 0} />
                                );
                            })}
                            {/* Valor real de quem foi recortado, para o recorte informar em vez
                                de esconder. */}
                            {recortada && (
                                <text x={xBarra(i) + larguraBarra / 2} y={M.top - 3}
                                      textAnchor="middle" fontSize={10} fill={TEXTO_1}>
                                    ↑ {fmtBRL(totalBarra, decimais)}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* linha de tendência: 2px, tom neutro, para não ser lida como uma terceira
                    série categórica — ela é uma anotação sobre o total, não um dado novo.
                    y preso ao plot: reta saindo pelo topo enganaria mais do que informaria. */}
                {reta && (() => {
                    const presa = (v) => Math.min(y(0), Math.max(M.top, y(v)));
                    return (
                        <line
                            x1={xBanda(reta.de) + banda / 2} y1={presa(reta.em(reta.de))}
                            x2={xBanda(reta.ate) + banda / 2} y2={presa(reta.em(reta.ate))}
                            stroke={TEXTO_1} strokeWidth={2} strokeLinecap="round" opacity={0.55}
                        />
                    );
                })()}

                {/* linha da meta, só no modo unitário */}
                {metaVisivel && (
                    <g>
                        <line x1={M.left} x2={M.left + larguraPlot} y1={y(meta)} y2={y(meta)}
                              stroke={TEXTO_1} strokeWidth={1} strokeDasharray="5 4" opacity={0.55} />
                        <text x={M.left + larguraPlot + 6} y={y(meta) + 4} fontSize={11} fill={TEXTO_2}>
                            meta {fmtBRL(meta)}/h
                        </text>
                    </g>
                )}

                {/* rótulos diretos: identidade sem depender só da cor, fora da área do plot */}
                {(() => {
                    const vInfra = valor(ultimo, 'infra');
                    const vStt = valor(ultimo, 'stt');
                    const vLlm = valor(ultimo, 'llm');
                    const xTexto = M.left + larguraPlot + 6;
                    const yLlm = y(vInfra + vStt + vLlm / 2);
                    const yStt = y(vInfra + vStt / 2);
                    const yInfra = y(vInfra / 2);
                    // O rótulo da meta ocupa a mesma coluna à direita: quem cair perto dele sai.
                    const longe = (yy) => !metaVisivel || Math.abs(yy - y(meta)) >= 13;
                    return (
                        <g>
                            {longe(yLlm) && (
                                <text x={xTexto} y={yLlm + 4} fontSize={11} fill={TEXTO_2}>LLM</text>
                            )}
                            {longe(yStt) && (
                                <text x={xTexto} y={yStt + 4} fontSize={11} fill={TEXTO_2}>Transcr.</text>
                            )}
                            {temInfra && vInfra > 0 && longe(yInfra) && (
                                <text x={xTexto} y={yInfra + 4} fontSize={11} fill={TEXTO_2}>Infra</text>
                            )}
                        </g>
                    );
                })()}

                {/* rótulos do eixo x */}
                {points.map((p, i) => (
                    (i % salto === 0 || i === points.length - 1) ? (
                        <text key={`r-${p.bucket}`} x={xBanda(i) + banda / 2} y={alturaSvg - mBottom + 16}
                              textAnchor="middle" fontSize={11} fill={TEXTO_2}>
                            {rotulo(p.bucket, granularity)}
                        </text>
                    ) : null
                ))}

                {/* Marcadores: número dentro de um disco, na faixa reservada embaixo. O
                    número é a chave para a lista de marcos, que é a legenda — texto solto no
                    eixo não caberia, e rotacionado não se lê. O <title> dá o texto no hover
                    nativo, que é o que salva o gráfico diário, onde a lista não está ao lado. */}
                {[...marcosPorColuna.entries()].map(([i, ms]) => {
                    const cx = xBanda(i) + banda / 2;
                    const cy = alturaSvg - 9;
                    // Até 2 marcos, mostra os números; a partir de 3, a faixa "n–m", senão o
                    // rótulo fica mais largo que a banda e invade a coluna vizinha.
                    const rot = ms.length <= 2
                        ? ms.map((m) => m.n).join('·')
                        : `${ms[0].n}–${ms[ms.length - 1].n}`;
                    const largo = 9 + (rot.length - 1) * 3.2;
                    return (
                        <g key={`mk-${i}`}>
                            <title>{ms.map((m) => `${m.n}. ${m.texto}`).join('\n')}</title>
                            <rect x={cx - largo} y={cy - 8} width={largo * 2} height={16} rx={8}
                                  fill={TEXTO_1} opacity={0.82} />
                            <text x={cx} y={cy + 4} textAnchor="middle" fontSize={10}
                                  fontWeight={600} fill={SUPERFICIE}>{rot}</text>
                        </g>
                    );
                })}

                {/* alvo do hover: a banda inteira, bem maior que a barra */}
                {points.map((p, i) => (
                    <rect key={`h-${p.bucket}`} x={xBanda(i)} y={M.top} width={banda} height={alturaPlot}
                          fill="transparent" tabIndex={0}
                          onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)}
                          onMouseLeave={() => setHover(null)} onBlur={() => setHover(null)} />
                ))}
            </svg>

            {hover != null && points[hover] && (() => {
                const p = points[hover];
                const esquerda = xBanda(hover) + banda / 2 > larguraUsada / 2;
                return (
                    <div className="gc-tooltip"
                         style={{
                             left: esquerda ? undefined : xBanda(hover) + banda / 2 + 12,
                             right: esquerda ? larguraUsada - (xBanda(hover) + banda / 2) + 12 : undefined,
                         }}>
                        <div className="gc-tt-titulo">{rotulo(p.bucket, granularity)}</div>
                        {p.transcriptions === 0 && (
                            <div className="gc-tt-nome">sem atendimentos</div>
                        )}
                        {tendencia && ralo(p) && (
                            <div className="gc-tt-nome">
                                volume baixo — fora da tendência
                            </div>
                        )}
                        {p.transcriptions > 0 && (
                            <>
                                <div className="gc-tt-linha">
                                    <span className="gc-tt-valor">{fmtBRL(valor(p, 'total'), decimais)}</span>
                                    <span className="gc-tt-nome">total{sufixo}</span>
                                </div>
                                <div className="gc-tt-linha">
                                    <span className="gc-tt-traco" style={{ background: COR_LLM }} />
                                    <span className="gc-tt-valor">{fmtBRL(valor(p, 'llm'), decimais)}</span>
                                    <span className="gc-tt-nome">LLM</span>
                                </div>
                                <div className="gc-tt-linha">
                                    <span className="gc-tt-traco" style={{ background: COR_STT }} />
                                    <span className="gc-tt-valor">{fmtBRL(valor(p, 'stt'), decimais)}</span>
                                    <span className="gc-tt-nome">transcrição</span>
                                </div>
                                {temInfra && p.infra_attributed !== false && (
                                    <div className="gc-tt-linha">
                                        <span className="gc-tt-traco" style={{ background: COR_INFRA }} />
                                        <span className="gc-tt-valor">{fmtBRL(valor(p, 'infra'), decimais)}</span>
                                        <span className="gc-tt-nome">
                                            {temQuebra ? 'infra por uso' : 'infra'}
                                        </span>
                                    </div>
                                )}
                                {temInfra && p.infra_attributed === false && (
                                    <div className="gc-tt-nome">
                                        infra não atribuída — volume baixo demais para diluir
                                        custo fixo
                                    </div>
                                )}
                            </>
                        )}
                        {marcosPorColuna.has(hover) && (
                            <div className="gc-tt-marcos">
                                {marcosPorColuna.get(hover).map((m) => (
                                    <div key={m.data} className="gc-tt-marco">
                                        <span className="gc-tt-marco-n">{m.n}</span>{m.texto}
                                    </div>
                                ))}
                            </div>
                        )}
                        {p.transcriptions > 0 && (
                            <div className="gc-tt-pe">
                                {p.transcriptions.toLocaleString('pt-BR')} atendimentos ·{' '}
                                {p.hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
}

export default GraficoCustos;
